// The maintenance "Confirmed with" / "Confirmed on" pair — SEED, BUILD and RENDER, as pure functions.
//
// 0.12.7 PART D (found 2026-09-15, prod-confirmed on VH-JOK). The form used to seed its
// "Confirmed with" input from the STORED note, which was already the rendered sentence, and then
// wrap whatever was in the input again on save:
//
//     "Maintenance confirmed with Maintenance confirmed with No ETA given by Jared on 2026-09-15 on 2026-07-24"
//
// and it seeded "Confirmed on" from the booking's own date rather than from the stored
// booking_confirmed_at, so an unedited save silently moved a correct confirmation date BACK to an
// older one (VH-JOK f2b34753: 2026-09-14T23:00Z → 2026-07-23T23:00Z in two edits ninety seconds
// apart). bookings.service's buildWarnings treats that field as the record of how and when a
// maintenance booking was confirmed, so that was the compliance record degrading, not a label.
//
// ⚠ THE TRANSFERABLE RULE. A field whose stored value is a FORMATTED RENDERING of user input will
// be corrupted by any form that seeds its editor from the stored value. The round trip is only
// safe when the stored value is the raw input. So from 0.12.7 the column stores what was TYPED,
// and the sentence is rendered at read time by renderMaintConfirmation — the one place it is built.
// Same family as summariseWaitlisted phrase-matching the audit reason in 0.12.6: a string that
// looks like data is really a rendering, and something downstream treated it as data.
//
// ⚠ LEGACY ROWS ARE LEFT EXACTLY AS STORED. Rows written before 0.12.7 hold the rendered sentence,
// some of them already nested. Cleaning those up is Ben's call and is NOT done here. They are
// recognised by the exact shape the old form always produced ("Maintenance confirmed with … on
// YYYY-MM-DD") and only when booking_confirmed_via is the form's own 'maintenance', and they are:
//   • RENDERED VERBATIM — never wrapped a second time;
//   • SEEDED by exact parse of ONE layer, failing CLOSED to the whole stored string for anything
//     nested or unparseable, never half-stripping a prefix;
//   • SAVED BYTE FOR BYTE UNCHANGED when neither input was edited.
//
// No React, no vite-only imports — plain TS, so database/verify-maint-confirmation.ts can import
// it. theme.ts has no imports of its own.

import { dateKeyInTz, localToUtcIso } from '../../components/ops-board/theme';

/** The booking_confirmed_via value the maintenance form writes. */
export const MAINT_CONFIRMED_VIA = 'maintenance';

const LEGACY_PREFIX = 'Maintenance confirmed with ';
// Exactly what the pre-0.12.7 form wrote: `Maintenance confirmed with ${text} on ${YYYY-MM-DD}`.
const LEGACY_SENTENCE = /^Maintenance confirmed with (.+) on (\d{4}-\d{2}-\d{2})$/;

/** The three stored columns this module reads (a TaskAssetAllocation, or the relevant slice of one). */
export interface MaintConfirmationFields {
  booking_confirmation_note: string | null;
  booking_confirmed_via: string | null;
  booking_confirmed_at: string | null;
}

export interface MaintConfirmationSeed {
  /** What the "Confirmed with" input opens holding. */
  withText: string;
  /** What the "Confirmed on" input opens holding: the STORED confirmation date, else the fallback. */
  date: string;
  /** The stored row the seed came from, so an unedited save can send it back unchanged. */
  stored: MaintConfirmationFields | null;
  /** True when withText was parsed out of a pre-0.12.7 sentence rather than read raw. */
  legacyParsed: boolean;
}

export interface MaintConfirmationPayload {
  booking_confirmation_note: string;
  booking_confirmed_via?: string;
  booking_confirmed_at: string;
}

/** True for a note in the pre-0.12.7 rendered shape (nested ones included). */
export function isLegacyMaintSentence(note: string): boolean {
  return LEGACY_SENTENCE.test(note);
}

/**
 * Seed the form's two inputs from a stored allocation. `tz` is the booking's dep_timezone;
 * `fallbackDate` (the booking's own date) is used ONLY when nothing was ever recorded.
 */
export function seedMaintConfirmation(
  stored: MaintConfirmationFields | null | undefined,
  tz: string,
  fallbackDate: string,
): MaintConfirmationSeed {
  const at = stored?.booking_confirmed_at ?? null;
  // ⚠ D.3 — from the STORED confirmation instant, never from the booking's date.
  const date = at ? dateKeyInTz(at, tz) : fallbackDate;
  const note = stored?.booking_confirmation_note ?? null;
  if (!stored || !note) return { withText: '', date, stored: stored ?? null, legacyParsed: false };

  // A note the maintenance form did not write (an API caller, or another via) is raw text already.
  if (stored.booking_confirmed_via !== MAINT_CONFIRMED_VIA) return { withText: note, date, stored, legacyParsed: false };
  const m = LEGACY_SENTENCE.exec(note);
  if (!m) return { withText: note, date, stored, legacyParsed: false }; // raw input, 0.12.7 onwards
  // NESTED: the inner text is itself a sentence. Fail closed to the whole stored string.
  if (m[1].startsWith(LEGACY_PREFIX)) return { withText: note, date, stored, legacyParsed: false };
  return { withText: m[1], date, stored, legacyParsed: true };
}

/**
 * Build the allocation fields for a save. null ⇒ send no confirmation (the input is blank).
 * `tz` is the zone the "Confirmed on" date is read in (the booking's base timezone).
 */
export function buildMaintConfirmation(
  withText: string,
  date: string,
  tz: string,
  seed: MaintConfirmationSeed,
): MaintConfirmationPayload | null {
  const text = withText.trim();
  if (!text) return null;
  const s = seed.stored;
  const dateUnchanged = date === seed.date;
  const textUnchanged = !!s?.booking_confirmation_note && text === seed.withText.trim();

  // The instant: untouched unless the date input was actually changed. Re-deriving it from the
  // date would move any confirmation not recorded at 09:00 local, on a save nobody edited.
  const at = dateUnchanged && s?.booking_confirmed_at ? s.booking_confirmed_at : localToUtcIso(date, '09:00', tz);

  // The text: the stored note and via go back BYTE FOR BYTE when "Confirmed with" was not edited —
  // except a legacy sentence whose date was just changed, which would otherwise keep displaying the
  // old date inside it; that one is written raw so the render shows the new date.
  if (textUnchanged && !(seed.legacyParsed && !dateUnchanged)) {
    return { booking_confirmation_note: s!.booking_confirmation_note!, booking_confirmed_via: s!.booking_confirmed_via ?? undefined, booking_confirmed_at: at };
  }
  return { booking_confirmation_note: text, booking_confirmed_via: MAINT_CONFIRMED_VIA, booking_confirmed_at: at };
}

/**
 * The note as a reader sees it. `tz` is the booking's dep_timezone. The ONE place the sentence is
 * built; every surface that shows the note goes through here.
 */
export function renderMaintConfirmation(alloc: MaintConfirmationFields, tz: string): string | null {
  const note = alloc.booking_confirmation_note;
  if (!note) return null;
  if (alloc.booking_confirmed_via !== MAINT_CONFIRMED_VIA) return note; // not written by the maintenance form
  if (LEGACY_SENTENCE.test(note)) return note; // stored pre-0.12.7 as a sentence; shown exactly as stored
  const date = alloc.booking_confirmed_at ? dateKeyInTz(alloc.booking_confirmed_at, tz) : null;
  return `${LEGACY_PREFIX}${note}${date ? ` on ${date}` : ''}`;
}
